import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useDetections } from 'src/data-capture/hooks/file-uploads/useDetections';
import { useDetectedEntitiesStore } from 'src/data-capture/stores/useDetectedEntitiesStore';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { useFileUpload } from './useFileUpload';

type UseFileUploadWithEntitiesProps = {
  autoRefreshProcessing?: boolean;
};

export function useFileUploadWithEntities({
  autoRefreshProcessing = false,
}: UseFileUploadWithEntitiesProps = {}) {
  const [searchParams] = useSearchParams();
  const fileId = searchParams.get('fileId') ?? '';

  const { handleDetectionData } = useDetectedEntitiesStore();
  const { flagEnabledShowNonConflictingFields, flagEnabledPreventAddConflicts } = useFlags();

  const {
    file,
    isLoading: isFileLoading,
    error: fileError,
    setFileResolved,
  } = useFileUpload(fileId, { autoRefreshProcessing });

  const {
    detections,
    isLoading: isDetectLoading,
    isFetching: isDetectFetching,
    error: detectError,
    refetchDetections,
  } = useDetections({
    fileId: file?.id,
  });

  useEffect(() => {
    if (file != null) {
      handleDetectionData(file.id, detections, flagEnabledShowNonConflictingFields, {
        excludeInsertedEntityConflicts: flagEnabledPreventAddConflicts,
      });
    }
  }, [isDetectFetching, flagEnabledShowNonConflictingFields, flagEnabledPreventAddConflicts]);

  const isLoading = isFileLoading || isDetectLoading;
  const error = fileError || detectError;

  const refetch = async () => {
    await refetchDetections();
  };

  return {
    file,
    isLoading,
    error,
    refetch,
    setFileResolved,
  };
}
